﻿import {JSX, MouseEvent, MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useRef} from "react";
import {ButtonKind} from "../ButtonKind";
import buttonTypeClass from "../helpers/buttonTypeClass";

export interface ButtonProps {
	children?: ReactNode,
	kind?: ButtonKind,
	waiting?: boolean,
	small?: boolean,
	onClick?: MouseEventHandler,
	disabled?: boolean,
	className?: string;
	cancellable?: CancelButtonProps;
}

export interface CancelButtonProps {
	delay: number;
	label: string;
}

interface CancelInternals {
	cancelling: boolean;
	timeOut: number;
}

export default function Button(props: ButtonProps): JSX.Element {
	const {children, kind, waiting, small, onClick, disabled, className, cancellable} = props;
	const delay = cancellable?.delay ?? 0;
	const isDisabled = disabled || waiting;
	const klass = useMemo(() => className ?? buttonTypeClass(kind) + (small ? " form__button--small" : ""), [kind, small, className]);
	const cancelObject = useRef<CancelInternals>({cancelling: false, timeOut: 0});
	const buttonRef = useRef<HTMLButtonElement>(null);

	// A pending cancellable click must die with the button: firing onClick after unmount
	// replays an event whose currentTarget is gone.
	useEffect(() => () => clearTimeout(cancelObject.current.timeOut), []);

	const checkDisabled = useCallback(function checkDisabled(e: MouseEvent<HTMLButtonElement>) {
		//TODO
		//https://github.com/CleverCloud/clever-components/blob/master/src/components/cc-button/cc-button.js

		if (e.currentTarget?.getAttribute("aria-disabled") === "true") {
			e.preventDefault();
			e.stopPropagation();
			return;
		}

		if (!onClick) {
			return;
		}

		if (delay <= 0) {
			onClick(e);
			return;
		}

		const cancelModel = cancelObject.current;
		if (cancelModel.cancelling) {
			clearTimeout(cancelModel.timeOut);
			cancelModel.cancelling = false;

		} else {
			cancelModel.cancelling = true;
			cancelModel.timeOut = setTimeout(() => {
				cancelModel.cancelling = false;
				onClick(e);
			}, delay * 1000);
		}

	}, [onClick, delay]);

	return <button
		ref={buttonRef}
		type={kind == "primary" ? "submit" : "button"}
		className={klass}
		aria-disabled={isDisabled}
		onClick={checkDisabled}
		disabled={false}>
		{children}
		{waiting && <progress className="waiting"></progress>}
	</button>;
};
